import { useCallback, useEffect, useRef, useState } from "react";
import googleHealthService, { type HealthSyncGoals } from "../services/GoogleHealthService";
import type { GoogleHealthPayload } from "../types/googleHealthBridge.types";
import type { FlutterUserData } from "../types/flutterHealthBridge.types";
import type { HealthSyncApiResponse } from "../utils/healthSyncMapper";
import { extractHealthPayloadFromBridge } from "../utils/bridgeHealthPayload";
import {
  getFlutterUserData,
  isGluceraFlutterApp,
  waitForFlutterBridge,
} from "../utils/flutterHealthBridge";
import {
  isGluceraAndroidApp,
  isGluceraNativeApp,
  requestNativeGoogleHealthData,
  subscribeGoogleHealthData,
  subscribeGoogleHealthError,
} from "../utils/nativeHealthBridge";
import { getLocalDateString } from "../utils/homePageUtils";
import { getGluceraUserIdFromStorage } from "../utils/gluceraAuthCookies";

type UseGoogleHealthSyncOptions = {
  date?: string;
  /** Request bridge + POST/GET health-sync when Trends opens */
  fetchBridgeOnMount?: boolean;
  /** Merged into POST health_data rows (steps_goal, calories_goal). */
  goals?: HealthSyncGoals;
};

type UseGoogleHealthSyncResult = {
  healthSyncApi: HealthSyncApiResponse | null;
  /** Live fitness data from Flutter/Android bridge — Trends UI reads this first. */
  healthPayload: GoogleHealthPayload | null;
  isNativeApp: boolean;
  isAndroidApp: boolean;
  isSyncing: boolean;
  isBridgeLoading: boolean;
  isHealthSyncLoading: boolean;
  isHealthDataReady: boolean;
  hasBridgeHealthData: boolean;
  hasGoogleHealthData: boolean;
  lastSyncedAt: string | null;
  flutterUserData: FlutterUserData | null;
  flutterBridgeError: string | null;
  flutterBridgeFetchedAt: string | null;
  syncGoogleHealth: () => void;
  /** POST health-sync with steps_goal / calories_goal on the selected date row. */
  persistGoalsToBackend: (goals: HealthSyncGoals) => Promise<boolean>;
  isGoalsSyncing: boolean;
  debugPopupOpen: boolean;
  debugPopupLoading: boolean;
  debugPopupData: unknown;
  closeDebugPopup: () => void;
};

const BRIDGE_TIMEOUT_MS = 5000;

const bridgeFingerprint = (
  payload: GoogleHealthPayload,
  goals?: HealthSyncGoals,
): string =>
  JSON.stringify({
    rows: payload.health_data ?? [],
    goals: goals ?? null,
  });

const hasBridgePayload = (payload: GoogleHealthPayload | null | undefined): boolean =>
  Boolean(payload?.health_data?.length);

const applyBridgeHealthLocal = (
  payload: GoogleHealthPayload,
  setHealthPayload: (p: GoogleHealthPayload) => void,
  setLastSyncedAt: (t: string) => void,
) => {
  googleHealthService.saveCachedPayload(payload);
  setHealthPayload(payload);
  setLastSyncedAt(new Date().toLocaleTimeString());
};

export const useGoogleHealthSync = (
  { date, fetchBridgeOnMount = true, goals }: UseGoogleHealthSyncOptions = {},
): UseGoogleHealthSyncResult => {
  const requestDate = date || getLocalDateString();
  const [healthSyncApi, setHealthSyncApi] = useState<HealthSyncApiResponse | null>(() =>
    googleHealthService.loadCachedHealthSync(date || getLocalDateString()),
  );
  const [healthPayload, setHealthPayload] = useState<GoogleHealthPayload | null>(() =>
    googleHealthService.loadCachedPayload(),
  );
  const [isSyncing, setIsSyncing] = useState(false);
  const [isBridgeLoading, setIsBridgeLoading] = useState(false);
  const [isHealthSyncLoading, setIsHealthSyncLoading] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [flutterUserData, setFlutterUserData] = useState<FlutterUserData | null>(null);
  const [flutterBridgeError, setFlutterBridgeError] = useState<string | null>(null);
  const [flutterBridgeFetchedAt, setFlutterBridgeFetchedAt] = useState<string | null>(null);
  const [debugPopupOpen, setDebugPopupOpen] = useState(false);
  const [debugPopupLoading, setDebugPopupLoading] = useState(false);
  const [debugPopupData, setDebugPopupData] = useState<unknown>(null);
  const [isGoalsSyncing, setIsGoalsSyncing] = useState(false);

  const mountedRef = useRef(true);
  const bridgeRequestedRef = useRef(false);
  const persistInFlightRef = useRef(false);
  const fallbackFetchStartedRef = useRef(false);
  const bridgeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPostedFingerprintRef = useRef<string | null>(null);
  const goalsRef = useRef(goals);
  goalsRef.current = goals;
  const healthPayloadRef = useRef(healthPayload);
  healthPayloadRef.current = healthPayload;

  const closeDebugPopup = useCallback(() => {
    setDebugPopupOpen(false);
    setDebugPopupLoading(false);
  }, []);

  const applyHealthSyncResponse = useCallback(
    (response: HealthSyncApiResponse | null, forDate?: string) => {
      if (!response || !mountedRef.current) return;
      const cacheDate = forDate || requestDate;
      googleHealthService.saveCachedHealthSync(response, cacheDate);
      // Drop responses that belong to a different request day (stale race).
      const responseDate =
        response.date_local ||
        response.selected_date ||
        response.today_local ||
        response.today_overview?.date;
      if (responseDate && responseDate !== cacheDate) return;
      setHealthSyncApi(response);
    },
    [requestDate],
  );

  const clearBridgeTimeout = useCallback(() => {
    if (bridgeTimeoutRef.current) {
      clearTimeout(bridgeTimeoutRef.current);
      bridgeTimeoutRef.current = null;
    }
  }, []);

  /** GET /health-sync/ — fallback when bridge data is unavailable. */
  const refreshHealthSyncFromApi = useCallback(async () => {
    const response = await googleHealthService.fetchHealthSync(requestDate);
    applyHealthSyncResponse(response, requestDate);
    return response;
  }, [applyHealthSyncResponse, requestDate]);

  const fetchHealthSyncFallback = useCallback(async () => {
    if (fallbackFetchStartedRef.current) return;
    fallbackFetchStartedRef.current = true;
    setIsHealthSyncLoading(true);
    try {
      await refreshHealthSyncFromApi();
    } finally {
      if (mountedRef.current) {
        setIsHealthSyncLoading(false);
      }
    }
  }, [refreshHealthSyncFromApi]);

  /** Bridge payload → POST (save DB). UI already shows bridge data — no GET wait. */
  const persistBridgeToBackend = useCallback(
    async (payload: GoogleHealthPayload) => {
      if (!payload.health_data?.length || !mountedRef.current) return;
      if (persistInFlightRef.current) return;

      const fingerprint = bridgeFingerprint(payload, goalsRef.current);
      persistInFlightRef.current = true;

      try {
        if (fingerprint !== lastPostedFingerprintRef.current) {
          const postResult = await googleHealthService.syncToBackend(payload, {
            date: requestDate,
            goals: goalsRef.current,
          });
          if (postResult.ok) {
            lastPostedFingerprintRef.current = fingerprint;
            if (postResult.response) {
              applyHealthSyncResponse(postResult.response, requestDate);
            }
          }
        }
      } finally {
        persistInFlightRef.current = false;
      }
    },
    [applyHealthSyncResponse, requestDate],
  );

  /**
   * Explicit goal edit → POST health-sync with steps_goal / calories_goal on health_data.
   * Uses cached bridge payload when present, otherwise a minimal row for the date.
   */
  const persistGoalsToBackend = useCallback(
    async (goals: HealthSyncGoals): Promise<boolean> => {
      if (!mountedRef.current) return false;
      if (
        (goals.steps_goal == null || goals.steps_goal <= 0) &&
        (goals.calories_goal == null || goals.calories_goal <= 0)
      ) {
        return false;
      }

      goalsRef.current = goals;
      setIsGoalsSyncing(true);

      const existing = healthPayloadRef.current;
      const payload: GoogleHealthPayload = existing?.health_data?.length
        ? existing
        : {
            user_id: getGluceraUserIdFromStorage() ?? undefined,
            health_data: [],
          };

      try {
        // Allow re-POST when only goals changed (fingerprint guard would block otherwise).
        lastPostedFingerprintRef.current = null;
        const postResult = await googleHealthService.syncToBackend(payload, {
          date: requestDate,
          goals,
        });
        if (postResult.ok) {
          lastPostedFingerprintRef.current = bridgeFingerprint(payload, goals);
          if (postResult.response) {
            applyHealthSyncResponse(postResult.response, requestDate);
          }
          return true;
        }
        return false;
      } finally {
        if (mountedRef.current) setIsGoalsSyncing(false);
      }
    },
    [applyHealthSyncResponse, requestDate],
  );

  const scheduleBridgeFallback = useCallback(() => {
    clearBridgeTimeout();
    bridgeTimeoutRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      setIsBridgeLoading(false);
      void fetchHealthSyncFallback();
    }, BRIDGE_TIMEOUT_MS);
  }, [clearBridgeTimeout, fetchHealthSyncFallback]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      bridgeRequestedRef.current = false;
      fallbackFetchStartedRef.current = false;
      clearBridgeTimeout();
    };
  }, [clearBridgeTimeout]);

  /** Flutter/Android → window.GluceraWeb.onHealthData */
  useEffect(() => {
    const unsubData = subscribeGoogleHealthData((payload) => {
      if (!mountedRef.current) return;
      clearBridgeTimeout();
      applyBridgeHealthLocal(payload, setHealthPayload, setLastSyncedAt);
      setIsBridgeLoading(false);
      void persistBridgeToBackend(payload);
    });

    const unsubError = subscribeGoogleHealthError((message) => {
      if (!mountedRef.current) return;
      setFlutterBridgeError(message);
      setIsBridgeLoading(false);
      void fetchHealthSyncFallback();
    });

    return () => {
      unsubData();
      unsubError();
    };
  }, [
    clearBridgeTimeout,
    fetchHealthSyncFallback,
    persistBridgeToBackend,
  ]);

  /**
   * Trends open → bridge first (live UI), POST background, GET only as fallback.
   */
  useEffect(() => {
    if (!fetchBridgeOnMount || bridgeRequestedRef.current) return;
    bridgeRequestedRef.current = true;
    fallbackFetchStartedRef.current = false;

    void (async () => {
      const cached = googleHealthService.loadCachedPayload();
      if (cached?.health_data?.length && mountedRef.current) {
        applyBridgeHealthLocal(cached, setHealthPayload, setLastSyncedAt);
        void persistBridgeToBackend(cached);
      }

      const nativeReady =
        isGluceraAndroidApp() ||
        isGluceraFlutterApp() ||
        (await waitForFlutterBridge(2500));

      if (!nativeReady) {
        await fetchHealthSyncFallback();
        return;
      }

      setIsBridgeLoading(true);
      scheduleBridgeFallback();

      await requestNativeGoogleHealthData();

      const userData = await getFlutterUserData();
      const fromUserData = extractHealthPayloadFromBridge(userData);
      if (fromUserData && mountedRef.current) {
        clearBridgeTimeout();
        applyBridgeHealthLocal(fromUserData, setHealthPayload, setLastSyncedAt);
        setIsBridgeLoading(false);
        void persistBridgeToBackend(fromUserData);
      }
    })();
  }, [
    clearBridgeTimeout,
    fetchBridgeOnMount,
    fetchHealthSyncFallback,
    persistBridgeToBackend,
    scheduleBridgeFallback,
  ]);

  /** GET health-sync for the request day on entry and whenever that day changes. */
  useEffect(() => {
    fallbackFetchStartedRef.current = false;
    lastPostedFingerprintRef.current = null;
    // Swap cache when the request day changes so yesterday never paints as today.
    setHealthSyncApi(googleHealthService.loadCachedHealthSync(requestDate));
    setIsHealthSyncLoading(true);
    void refreshHealthSyncFromApi().finally(() => {
      if (mountedRef.current) setIsHealthSyncLoading(false);
    });
  }, [requestDate, refreshHealthSyncFromApi]);

  /** Sync button — getUserData popup only (verify); no POST/GET. */
  const syncGoogleHealth = useCallback(() => {
    setIsSyncing(true);
    setFlutterBridgeError(null);
    setDebugPopupOpen(true);
    setDebugPopupLoading(true);
    setDebugPopupData(null);

    void (async () => {
      try {
        const flutterReady =
          isGluceraFlutterApp() || (await waitForFlutterBridge(2000));

        if (!flutterReady || !isGluceraFlutterApp()) {
          const msg = "flutter_inappwebview is not available";
          setFlutterBridgeError(msg);
          setDebugPopupData({ error: msg });
          return;
        }

        const handler = window.flutter_inappwebview;
        if (!handler?.callHandler) {
          const msg = "flutter_inappwebview.callHandler missing";
          setFlutterBridgeError(msg);
          setDebugPopupData({ error: msg });
          return;
        }

        const data = await handler.callHandler("getUserData");
        if (!mountedRef.current) return;

        setFlutterUserData(data as FlutterUserData);
        setFlutterBridgeFetchedAt(new Date().toLocaleTimeString());
        setDebugPopupData(data);

        const healthFromResponse = extractHealthPayloadFromBridge(data);
        if (healthFromResponse) {
          applyBridgeHealthLocal(healthFromResponse, setHealthPayload, setLastSyncedAt);
        }
      } catch (error) {
        if (!mountedRef.current) return;
        const msg =
          error instanceof Error ? error.message : "getUserData call failed";
        setFlutterBridgeError(msg);
        setDebugPopupData({ error: msg });
      } finally {
        if (mountedRef.current) {
          setDebugPopupLoading(false);
          setIsSyncing(false);
        }
      }
    })();
  }, []);

  const hasBridgeHealthData = hasBridgePayload(healthPayload);
  const hasServerHealth = Boolean(
    healthSyncApi?.has_data || (healthSyncApi?.daily_graph?.length ?? 0) > 0,
  );

  return {
    healthSyncApi,
    healthPayload,
    isNativeApp: isGluceraNativeApp(),
    isAndroidApp: isGluceraAndroidApp(),
    isSyncing,
    isBridgeLoading,
    isHealthSyncLoading,
    isHealthDataReady:
      hasBridgeHealthData || (!isBridgeLoading && !isHealthSyncLoading),
    hasBridgeHealthData,
    hasGoogleHealthData: hasBridgeHealthData || hasServerHealth,
    lastSyncedAt,
    flutterUserData,
    flutterBridgeError,
    flutterBridgeFetchedAt,
    syncGoogleHealth,
    persistGoalsToBackend,
    isGoalsSyncing,
    debugPopupOpen,
    debugPopupLoading,
    debugPopupData,
    closeDebugPopup,
  };
};
